import { Type } from 'class-transformer';
import {
	ArrayMaxSize,
	ArrayMinSize,
	IsArray,
	IsBoolean,
	IsInt,
	IsObject,
	IsOptional,
	IsString,
	Max,
	Min,
	ValidateNested,
} from 'class-validator';
import type { InstantlyWebhookState } from '../../constants/instantly';
import type { InstantlyMailboxUseStatus } from '../../constants/instantly-settings';
import type { InstantlyConnectionEntity } from '../entity/instantly-connection.entity';

type InstantlyConnectionFieldsModel = Pick<
	InstantlyConnectionEntity,
	'workspaceId' | 'workspaceName' | 'planName' | 'leadLimit' | 'leadCount' | 'lastSyncError' | 'connectedByName'
>;

/**
 * PD-17608 — the Instantly settings card: whether the tenant's workspace is
 * connected, which workspace and plan the key belongs to, and the state of
 * Workd's webhooks there. The API key is never returned.
 */
export type InstantlyConnectionStatusDto =
	| { connected: false }
	| (InstantlyConnectionFieldsModel & {
			connected: true;
			webhookState: InstantlyWebhookState;
			lastSyncDate: string | null;
			connectedDate: string;
		});

/**
 * Connect (or reconnect) Instantly. On reconnect a blank key keeps the stored
 * one; on a first connect the key is required (enforced in the service).
 */
export class InstantlyConnectRequestDto {
	@IsOptional()
	@IsString()
	public apiKey?: string;
}

/** What a disconnect did in Instantly before the key was removed. */
export interface InstantlyDisconnectResultDto {
	success: boolean;
	/** False when Instantly could not be reached or refused, so a webhook may remain there. */
	webhooksRemoved: boolean;
}

/**
 * PD-17609 — the defaults a new campaign starts with (Settings → Instantly).
 * The sequence engine (PD-17614) copies them onto each Instantly campaign it
 * creates; a campaign can change its own copy.
 */
export class InstantlyCampaignDefaultsDto {
	/** Plain text emails (no HTML formatting, no images). */
	@IsBoolean()
	public plainText!: boolean;

	/** Open and click tracking; off by default because tracking hurts cold-email delivery. */
	@IsBoolean()
	public trackOpensAndClicks!: boolean;

	/** Instantly's List-Unsubscribe header on every email. */
	@IsBoolean()

	/** Pause a person on an out-of-office reply until they are back (never end the run). */
	@IsBoolean()
	public pauseOnOutOfOffice!: boolean;

	/** Days emails go out, numbered as Instantly's schedule numbers them (0 = Sunday). */
	@IsArray()
	@ArrayMinSize(1)
	@ArrayMaxSize(7)
	@IsInt({ each: true })
	@Min(0, { each: true })
	@Max(6, { each: true })
	public sendingDays!: Array<number>;

	/** First hour emails may go out, in the person's own time zone (0-23). */
	@IsInt()
	@Min(0)
	@Max(23)
	public startHour!: number;

	/** Hour sending stops, in the person's own time zone (1-24, after startHour). */
	@IsInt()
	@Min(1)
	@Max(24)
	public endHour!: number;

	/** New people a campaign starts each day (Instantly's daily_max_leads). */
	@IsInt()
	@Min(1)
	@Max(10_000)
	public newPeoplePerDay!: number;
}

/**
 * The tenant's Instantly settings, stored in the `instantly.settings` instance
 * setting. `replyMapping` holds only the rows an admin set: key
 * `status:<lt_interest_status>` or `label:<lead label id>` → a Reply Category
 * catalog value id, or null for "no category". A key not stored falls back to
 * its default meaning.
 */
export class InstantlySettingsDto {
	@IsObject()
	public replyMapping!: Record<string, number | null>;

	@ValidateNested()
	@Type(() => InstantlyCampaignDefaultsDto)
	public campaignDefaults!: InstantlyCampaignDefaultsDto;

	/** Days after a person's run ends before Workd removes them from Instantly. */
	@IsInt()
	@Min(0)
	@Max(90)
	public rotationDelayDays!: number;

	/** Instantly campaigns an admin chose not to bring into Workd. */
	@IsArray()
	@ArrayMaxSize(1000)
	@IsString({ each: true })
	public ignoredCampaignIds!: Array<string>;
}

/** PD-17609 — leave an Instantly campaign out of Workd's list, or list it again. */
export class InstantlyIgnoreCampaignRequestDto {
	@IsBoolean()
	public ignored!: boolean;
}

/** One sender group: the mailboxes carrying one Instantly account tag. */
export interface InstantlySenderGroupDto {
	tagId: string;
	tag: string;
	/** The sender name most of the group's mailboxes use; null when they carry none. */
	sendsAs: string | null;
	mailboxCount: number;
	healthyCount: number;
	/** Sum of the healthy mailboxes' daily limits. */
	dailyCapacity: number;
	/** PD-17734 — mailboxes that cannot send, and why; Workd leaves them out of campaigns. */
	problemMailboxes: Array<{ email: string; reason: string }>;
	/** Emails the group's mailboxes sent today (follow-ups included); null when Instantly did not say. */
	sentToday: number | null;
	/** Running Instantly campaigns that send from this group (by tag or by its mailboxes). */
	campaignCount: number;
}

/** Mailboxes with no tag: never used by Workd. */
export interface InstantlyUntaggedMailboxesDto {
	mailboxCount: number;
	sendsAs: string | null;
}

/** One group's mailbox use over its recent sending days. */
export interface InstantlyMailboxUseDto {
	tag: string;
	status: InstantlyMailboxUseStatus;
	/** Sending days counted (at most INSTANTLY_MAILBOX_USE_DAYS). */
	days: number;
	/** Lowest, typical (median) and highest typical day across the healthy mailboxes that sent. */
	low: number;
	typical: number;
	high: number;
	/** The healthy mailboxes' usual daily limit (median). */
	mailboxLimit: number | null;
	/** Emails sent as a 0-1 share of the healthy mailboxes' combined limit over those days. */
	limitUsedShare: number | null;
	/** Emails a day the group could still send on a typical day. */
	roomPerDay: number;
	/** Days the group sent at least INSTANTLY_AT_LIMIT_SHARE of its capacity. */
	atLimitDays: number;
	/** Healthy mailboxes that sent nothing on any of those days. */
	idleHealthyCount: number;
	/** Unhealthy mailboxes (skipped by Instantly) in the group. */
	unhealthyCount: number;
}

/** One line of "Needs a look" on the mailbox side. */
export interface InstantlyNeedsALookDto {
	kind: 'unhealthy_mailbox' | 'idle_mailbox' | 'uneven_mailbox' | 'group_at_limit';
	/** A mailbox address, or the group's tag for group_at_limit. */
	subject: string;
	/** Why a mailbox is unhealthy (Instantly's account or warm-up status). */
	reason: string | null;
	/** Recent sending days in a row the mailbox sent nothing. */
	idleDays: number | null;
	/** uneven_mailbox: the mailbox's typical day. group_at_limit: the 0-1 share of the limit used. */
	value: number | null;
	/** uneven_mailbox: the group's typical day. group_at_limit: days at the limit. */
	compareTo: number | null;
	/** group_at_limit: sending days counted. */
	days: number | null;
}

/** How an Instantly campaign relates to Workd (Title Case: shown as a status). */
export type InstantlyCampaignLinkState = 'Workd Campaign' | 'Brought In' | 'Not In Workd' | 'Ignored';

export interface InstantlyBringInProgressDto {
	status: 'Queued' | 'Running' | 'Done' | 'Failed';
	peopleBroughtIn: number;
	/** People matched to a person already in Workd. */
	peopleMatched: number;
	startedAt: string | null;
	finishedAt: string | null;
	error: string | null;
}

/** One campaign in the Instantly workspace. */
export interface InstantlyCampaignSummaryDto {
	id: string;
	name: string;
	/** Instantly's status, Title Case (Active, Paused, Completed, ...). */
	status: string;
	/** People in the campaign; null when Instantly did not say. */
	peopleCount: number | null;
	linkState: InstantlyCampaignLinkState;
	/** The Workd campaign it is linked to, if any. */
	workdCampaignId: number | null;
	/** Bringing it in: where that stands; null when never started. */
	bringIn: InstantlyBringInProgressDto | null;
}

/** One row of the reply status mapping. */
export interface InstantlyReplyStatusRowDto {
	key: string;
	label: string;
	kind: 'built_in' | 'label';
	/** The Reply Category catalog value it maps to; null for "no category". */
	catalogValueId: number | null;
}

export interface InstantlyReplyCategoryOptionDto {
	id: number;
	name: string;
}

export interface InstantlyPeopleSlotsDto {
	used: number | null;
	limit: number | null;
}

/** PD-17609 — everything the Instantly settings page shows. */
export interface InstantlyOverviewDto {
	connection: InstantlyConnectionStatusDto;
	peopleSlots: InstantlyPeopleSlotsDto;
	senderGroups: Array<InstantlySenderGroupDto>;
	untagged: InstantlyUntaggedMailboxesDto;
	mailboxUse: Array<InstantlyMailboxUseDto>;
	/** Newest stored day of mailbox use (YYYY-MM-DD); null before the first nightly run. */
	mailboxUseThrough: string | null;
	needsALook: Array<InstantlyNeedsALookDto>;
	campaigns: Array<InstantlyCampaignSummaryDto>;
	replyStatuses: Array<InstantlyReplyStatusRowDto>;
	replyCategories: Array<InstantlyReplyCategoryOptionDto>;
	settings: InstantlySettingsDto;
	/** Why parts of the page could not be read from Instantly; null when all of it was. */
	readError: string | null;
}
